import { CircularProgressOverlay } from '@campfire/circular-progress-overlay';
import { Grid, FormControl, InputLabel, Select, MenuItem, Checkbox, Box, Chip, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import React, { useState, useEffect } from 'react';
import { VolunteerCommonTag } from '../../__generated__/VolunteerCommonTag';
import { useVolunteerCommonProfileContext } from '../../VolunteerCommonProfileContext';
import { useVolunteerProfileActions } from '../../volunteer-profile-actions';
import { InfoBlock } from '../../../volunteers/incoming-volunteer/components/IncomingVolunteerInfoBlock';

const DEFAULT_TAG_COLOUR = '#796fff';

const useStyles = makeStyles(() =>
  createStyles({
    chips: {
      paddingLeft: 6,
    },
    tagChip: {
      color: '#ffffff',
    },
    groupLabel: {
      fontSize: 13,
      fontWeight: 600,
      color: '#444444',
      paddingLeft: 16,
      paddingTop: 8,
      paddingBottom: 2,
    },
  })
);

export const VolunteerTags = ({
  volunteerId,
  volunteerTags,
  editable,
}: {
  volunteerId: string;
  volunteerTags: VolunteerCommonTag[];
  editable?: boolean;
}) => {
  const [tagsSelectOpen, setTagsSelectOpen] = useState(false);
  const [volunteerTagIds, setVolunteerTagIds] = useState<string[]>([]);
  const classes = useStyles();
  const { availableTags, updateParentVolunteerActions } = useVolunteerCommonProfileContext();
  const { runSaveVolunteerTags, runSaveVolunteerTagsLoading } = useVolunteerProfileActions();

  useEffect(() => {
    setVolunteerTagIds(volunteerTags.map((tag) => tag.volunteerTagId));
  }, [volunteerTags]);

  const handleSuccess = (tagIds: string[]) => {
    setVolunteerTagIds(tagIds);
    if (updateParentVolunteerActions) {
      const { updateVolunteerWithSavedTags } = updateParentVolunteerActions;
      if (updateVolunteerWithSavedTags) updateVolunteerWithSavedTags(tagIds);
    }
  };

  const heldTagIds = new Set(volunteerTagIds);
  const selectableTags = availableTags.filter((tag) => !tag.isArchived || heldTagIds.has(tag.volunteerTagId));

  const orderedTags = [...selectableTags].sort((a, b) => {
    const groupCompare = (a.tagGroup || '￿').localeCompare(b.tagGroup || '￿');
    return groupCompare !== 0 ? groupCompare : a.name.localeCompare(b.name);
  });

  const showTagsSelectField = !!selectableTags.length && editable;

  const renderMenuItems = () => {
    const items: JSX.Element[] = [];
    let lastGroup: string | null | undefined;

    orderedTags.forEach((tag) => {
      if (tag.tagGroup !== lastGroup) {
        lastGroup = tag.tagGroup;
        if (tag.tagGroup) {
          items.push(
            <Typography key={`group-${tag.tagGroup}`} className={classes.groupLabel}>
              {tag.tagGroup}
            </Typography>
          );
        }
      }
      items.push(
        <MenuItem key={tag.volunteerTagId} value={tag.volunteerTagId}>
          <Checkbox color='primary' checked={heldTagIds.has(tag.volunteerTagId)} />
          {tag.name}
          {tag.isArchived ? ' (archived)' : ''}
        </MenuItem>
      );
    });

    return items;
  };

  if (showTagsSelectField) {
    return (
      <>
        <CircularProgressOverlay isLoading={runSaveVolunteerTagsLoading} />
        <Box py={1}>
          <FormControl fullWidth>
            <InputLabel htmlFor='select-volunteer-tags' style={{ paddingLeft: 8, paddingTop: 4 }}>
              {`Volunteer Tags`}
            </InputLabel>
            <Select
              fullWidth
              open={tagsSelectOpen}
              onOpen={() => setTagsSelectOpen(true)}
              onClose={() => setTagsSelectOpen(false)}
              multiple
              variant='filled'
              value={volunteerTagIds}
              onChange={(event) => {
                const selectedTagIds = (event.target.value as string[]).filter((x) => !!x);
                runSaveVolunteerTags({
                  volunteerId,
                  volunteerTagIds: selectedTagIds,
                  handleSuccess,
                });
              }}
              renderValue={(tagIds) => (
                <Grid container spacing={1} className={classes.chips}>
                  {(tagIds as Array<string>).map((tagId) => {
                    const selectedTag = availableTags.find((x) => x.volunteerTagId === tagId);
                    return selectedTag ? (
                      <Grid item key={selectedTag.volunteerTagId}>
                        <Chip
                          className={classes.tagChip}
                          style={{
                            backgroundColor: selectedTag.colour || DEFAULT_TAG_COLOUR,
                            borderRadius: 4,
                            fontWeight: 500,
                          }}
                          label={selectedTag.name}
                        />
                      </Grid>
                    ) : null;
                  })}
                </Grid>
              )}
            >
              {renderMenuItems()}
            </Select>
          </FormControl>
        </Box>
      </>
    );
  }

  return volunteerTags.length ? (
    <InfoBlock label='Tags' value={volunteerTags.map((tag) => tag.name).join(', ')} />
  ) : null;
};
